import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/layout/Layout';
import { useFamilyStore } from '../stores/familyStore';
import { useAuthStore } from '../stores/authStore';
import { FamilyMembersList } from '../components/family/FamilyMembersList';
import { ChildrenList } from '../components/family/ChildrenList';
import { cn } from '../utils/cn';
import { colorSchemes, getColorScheme } from '../utils/colorSchemes';
import logger from '../utils/logger';
import { useUserPreferencesStore } from '../stores/userPreferencesStore';
import { usePermissions } from '../hooks/usePermissions';
import { getSettingsTabAccess } from '../utils/accessControl';
import { usePWAInstall } from '../hooks/usePWAInstall';

/**
 * Strona ustawień rodziny z kontrolą dostępu - Mobile First Design
 */
type SettingsTab = 'family' | 'members' | 'install' | 'appearance';

export const FamilySettings: React.FC = () => {
  const { user } = useAuthStore();
  const { family, members, isLoading, error, initialize, updateFamily, createFamily, updateAssignedHome } = useFamilyStore();
  const { preferences, updateColorScheme, loadPreferences, isLoading: preferencesLoading } = useUserPreferencesStore();
  const { 
    userRole,
    canEditFamilySettings
  } = usePermissions();
  
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    // Pobierz zapisany tab z sessionStorage
    const savedTab = sessionStorage.getItem('family-settings-active-tab');
    // Jeśli zapisany był stary tab 'children', przełącz na 'family'
    if (savedTab === 'children') return 'family';
    return (savedTab as SettingsTab) || 'family';
  });
  const [familyName, setFamilyName] = useState('');
  const [homeAName, setHomeAName] = useState('');
  const [homeBName, setHomeBName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Hook do instalacji PWA
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  
  // Dodajemy flagę, aby zapobiec wielokrotnym inicjalizacjom
  const initRef = useRef(false);
  
  useEffect(() => {
    if (user && !family && !isLoading && !initRef.current) {
      initRef.current = true;
      initialize();
    }
  }, [user, family, isLoading, initialize]);
  
  // Aktualizuj lokalne stany gdy rodzina się załaduje
  useEffect(() => {
    if (family) {
      setFamilyName(family.name);
      setHomeAName(family.home_a_name || 'Dom A');
      setHomeBName(family.home_b_name || 'Dom B');
    }
  }, [family]);
  
  // Załaduj preferencje użytkownika
  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user, loadPreferences]);
  
  // Sprawdź uprawnienia do tabów
  const tabAccess = getSettingsTabAccess(userRole);
  
  // Zapisz activeTab do sessionStorage za każdym razem, gdy się zmienia
  useEffect(() => {
    sessionStorage.setItem('family-settings-active-tab', activeTab);
  }, [activeTab]);

  // Nie przekierowuj do tworzenia rodziny – obsługujemy tworzenie w zakładce Ogólne
  
  const allTabs = [
    { id: 'family', label: 'Ogólne', icon: '🏠', access: 'family' },
    { id: 'members', label: 'Dostępy', icon: '👥', access: 'members' },
    { id: 'install', label: 'Instalacja', icon: '📱', access: 'install' },
    { id: 'appearance', label: 'Wygląd', icon: '🎨', access: 'appearance' }
  ];
  
  // Filtruj taby na podstawie uprawnień
  const tabs = allTabs.filter(tab => tabAccess[tab.access as keyof typeof tabAccess]);
  
  const handleSaveFamilySettings = async () => {
    setIsSaving(true);
    try {
      if (!family) {
        if (!familyName || !familyName.trim()) {
          alert('Podaj nazwę rodziny, aby ją utworzyć');
          return;
        }
        const familyId = await createFamily(familyName.trim());
        if (!familyId) return;
        // Po utworzeniu, update nazw domów jeśli różne od domyślnych
        if (homeAName || homeBName) {
          await updateFamily({
            home_a_name: homeAName,
            home_b_name: homeBName
          });
        }
      } else {
        await updateFamily({
          name: familyName,
          home_a_name: homeAName,
          home_b_name: homeBName
        });
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  // Usunięto zmianę hasła ze strony Ustawienia – dostępne w /account

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Ładowanie ustawień...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            <p>Wystąpił błąd: {error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Produkcja: pozwól wejść na ustawienia nawet bez istniejącej rodziny

  return (
    <Layout>
      <div className="bg-gray-50">
        {/* Mobile-first tab navigation - Sticky */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="px-4 py-2">
            <div className="grid grid-cols-4 gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as SettingsTab)}
                  className={cn(
                    "flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer select-none",
                    "transition-colors duration-100 active:scale-95",
                    "text-xs font-medium touch-manipulation relative",
                    activeTab === tab.id
                      ? "text-blue-600 bg-blue-50"
                      : "text-gray-500 hover:bg-gray-50"
                  )}
                >
                  <span className="text-lg mb-1">{tab.icon}</span>
                  <span className="leading-tight text-center">{tab.label}</span>
                  {activeTab === tab.id && (
                    <span className="absolute bottom-0 left-2 right-2 h-[2px] bg-blue-500 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Zawartość tabów */}
        <div className="p-3">
          {/* Tab: Ustawienia ogólne */}
          {activeTab === 'family' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-3 pt-4 pb-2">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">🏠</span>
                  Ustawienia ogólne
                </h2>
              </div>
              
              <div className="p-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nazwa rodziny</label>
                  <input 
                    type="text" 
                    value={familyName}
                    onChange={canEditFamilySettings ? (e) => setFamilyName(e.target.value) : undefined}
                    disabled={!canEditFamilySettings}
                    className={cn(
                      "w-full px-3 py-2 border rounded-lg text-sm",
                      canEditFamilySettings 
                        ? "border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        : "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                    )}
                    placeholder="Nazwij rodzinę..."
                  />
                </div>
                
                <div>
                  <h3 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                    <span className="mr-2">🏡</span>
                    Nazwy domów / opiekunów
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {(() => {
                      const scheme = getColorScheme(preferences.color_scheme);
                      return (
                        <>
                          {/* Dom A */}
                          <div className={`p-2 rounded-lg bg-gradient-to-r ${scheme.homeA.bg} border ${scheme.homeA.border}`}>
                            <h4 className={`text-xs font-medium ${scheme.homeA.text} flex items-center mb-1`}>
                              <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: scheme.homeA.color }}></div>
                              Dom A
                            </h4>
                            <input 
                              type="text" 
                              placeholder="Twoje imię lub adres"
                              value={homeAName}
                              onChange={canEditFamilySettings ? (e) => setHomeAName(e.target.value) : undefined}
                              disabled={!canEditFamilySettings}
                              className={cn(
                                "w-full text-xs rounded-lg px-2 py-1",
                                canEditFamilySettings 
                                  ? `bg-white/80 border ${scheme.homeA.border} focus:outline-none focus:ring-1 focus:ring-blue-500`
                                  : "bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed"
                              )}
                            />
                          </div>
                          
                          {/* Dom B */}
                          <div className={`p-2 rounded-lg bg-gradient-to-r ${scheme.homeB.bg} border ${scheme.homeB.border}`}>
                            <h4 className={`text-xs font-medium ${scheme.homeB.text} flex items-center mb-1`}>
                              <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: scheme.homeB.color }}></div>
                              Dom B
                            </h4>
                            <input 
                              type="text" 
                              placeholder="Imię lub adres drugiego opiekuna"
                              value={homeBName}
                              onChange={canEditFamilySettings ? (e) => setHomeBName(e.target.value) : undefined}
                              disabled={!canEditFamilySettings}
                              className={cn(
                                "w-full text-xs rounded-lg px-2 py-1",
                                canEditFamilySettings 
                                  ? `bg-white/80 border ${scheme.homeB.border} focus:outline-none focus:ring-1 focus:ring-blue-500`
                                  : "bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed"
                              )}
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Przypisanie admina do domu */}
                {canEditFamilySettings && family && (() => {
                  const scheme = getColorScheme(preferences.color_scheme);
                  const me = members.find(m => m.user_id === user?.id);
                  const myHome = me?.assigned_home || (me?.role === 'parent_a' ? 'home_a' : 'home_b');
                  return (
                    <div className="mt-2">
                      <h3 className="text-xs font-medium text-gray-700 mb-2">Mój dom</h3>
                      <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => updateAssignedHome('home_a')}
                          className={cn(
                            "flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors",
                            myHome === 'home_a'
                              ? "text-white"
                              : "bg-white text-gray-600 hover:bg-gray-50"
                          )}
                          style={myHome === 'home_a' ? { backgroundColor: scheme.homeA.color } : undefined}
                        >
                          <div className="w-2.5 h-2.5 rounded-full border border-white/50" style={{ backgroundColor: scheme.homeA.color }}></div>
                          {homeAName || 'Dom A'}
                        </button>
                        <button
                          type="button"
                          onClick={() => updateAssignedHome('home_b')}
                          className={cn(
                            "flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors",
                            myHome === 'home_b'
                              ? "text-white"
                              : "bg-white text-gray-600 hover:bg-gray-50"
                          )}
                          style={myHome === 'home_b' ? { backgroundColor: scheme.homeB.color } : undefined}
                        >
                          <div className="w-2.5 h-2.5 rounded-full border border-white/50" style={{ backgroundColor: scheme.homeB.color }}></div>
                          {homeBName || 'Dom B'}
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Przycisk Zapisz zmiany - tylko dla ustawień domów i nazwy rodziny */}
                <div className="flex justify-end pt-2 border-t mt-3">
                  {canEditFamilySettings && (
                    <button
                      onClick={handleSaveFamilySettings}
                      disabled={isSaving}
                      className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 transition-colors"
                    >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Zapisywanie...
                      </>
                    ) : (
                      <>Zapisz zmiany</>
                    )}
                    </button>
                  )}
                  {!canEditFamilySettings && (
                    <div className="text-xs text-gray-500 italic">
                      Tylko administrator może edytować ustawienia rodziny
                    </div>
                  )}
                </div>

                {/* Dzieci przeniesione do Ogólnych */}
                <div className="pt-2">
                  <h3 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                    <span className="mr-2">👫</span>
                    Dzieci/ Podopieczni
                  </h3>
                  {family ? (
                    <div>
                      <ChildrenList />
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-xs text-yellow-800">
                      Utwórz rodzinę, aby dodać dzieci.
                    </div>
                  )}
                </div>
                </div>
            </div>
          )}
          
          {/* Tab: Instalacja */}
          {activeTab === 'install' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-3 pt-4 pb-2">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">📱</span>
                  Instalacja aplikacji 2domy.pl
                </h2>
              </div>
              <div className="p-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <div>
                    <p className="text-xs text-blue-700">
                      Zainstaluj aplikację, aby mieć szybszy dostęp do kalendarza bez paska adresu przeglądarki.
                    </p>
                  </div>
                  <div className="mt-3">
                    <button
                      onClick={promptInstall}
                      disabled={!isInstallable || isInstalled}
                      className={cn(
                        "w-full py-2 px-4 rounded-lg text-sm font-medium flex items-center justify-center",
                        isInstallable && !isInstalled
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      )}
                    >
                      {isInstalled ? (
                        <>
                          <span className="mr-2">✅</span>
                          Aplikacja już zainstalowana
                        </>
                      ) : isInstallable ? (
                        <>
                          <span className="mr-2">📲</span>
                          Zainstaluj (Android)
                        </>
                      ) : (
                        <>
                          <span className="mr-2">⏳</span>
                          Instalacja niedostępna
                        </>
                      )}
                    </button>
                    <div className="text-xs text-blue-700 mt-2 text-center">
                      iOS: Safari → Udostępnij → „Do ekranu początkowego”.
                    </div>
                    {!isInstallable && !isInstalled && (
                      <p className="text-xs text-blue-600 mt-2 text-center">
                        Aby zainstalować aplikację, otwórz ją w przeglądarce Chrome lub Safari.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Tab: Dostępy */}
          {activeTab === 'members' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-3 pt-4 pb-2">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">👥</span>
                  Dostępy
                </h2>
              </div>
              <div className="p-2">
                <FamilyMembersList />
              </div>
            </div>
          )}
          
          {/* Tab: Wygląd */}
          {activeTab === 'appearance' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="px-3 pt-4 pb-2">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">🎨</span>
                  Schemat kolorystyczny
                </h2>
              </div>
              
              <div className="p-2">
                <div className="grid grid-cols-1 gap-2">
                  {Object.values(colorSchemes).map((scheme) => (
                    <div 
                      key={scheme.id}
                      onClick={async () => {
                        const success = await updateColorScheme(scheme.id);
                        if (success) {
                          logger.debug(`✅ Schemat ${scheme.id} zastosowany`);
                        }
                      }}
                      className={`p-2 border-2 rounded-lg cursor-pointer select-none touch-manipulation transition-colors duration-100 hover:shadow-md active:scale-98 ${
                        preferences.color_scheme === scheme.id 
                          ? 'border-blue-500 bg-blue-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-sm">{scheme.name}</h4>
                        {preferences.color_scheme === scheme.id && (
                          <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2">
                        {/* Podgląd Dom A */}
                        <div className={`flex-1 p-2 rounded-lg bg-gradient-to-r ${scheme.homeA.bg} border ${scheme.homeA.border}`}>
                          <div className={`text-xs ${scheme.homeA.text} font-medium flex items-center`}>
                            <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: scheme.homeA.color }}></div>
                            Dom A
                          </div>
                        </div>
                        
                        {/* Podgląd Dom B */}
                        <div className={`flex-1 p-2 rounded-lg bg-gradient-to-r ${scheme.homeB.bg} border ${scheme.homeB.border}`}>
                          <div className={`text-xs ${scheme.homeB.text} font-medium flex items-center`}>
                            <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: scheme.homeB.color }}></div>
                            Dom B
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {preferencesLoading && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span className="text-blue-700 text-xs font-medium">Zapisywanie...</span>
                  </div>
                )}
                
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 text-center">
                    ✨ Schemat zostaje automatycznie zapisany po wyborze
                  </p>
                </div>
              </div>
            </div>
          )}
          
          
        </div>
      </div>
    </Layout>
  );
};